// "Pin to Workspace" toggle + auto-saving Personal Notes textarea.
//
// Authenticated users get the real controls (synced to user_workspace).
// Guests get a single subtle, non-intrusive sign-in CTA in the same slot —
// no nags, no modal walls.
import { isAuthed } from "../lib/auth.js";
import { setPinned, saveNotes } from "../lib/data.js";
import { debounce } from "./helpers.js";

// item: an overlaid record (has id, is_pinned, user_notes_text).
// collectionType: "verbs" | "grammar_articles".
// onSignInClick: callback to open the auth panel.
export function renderNotesPin(item, collectionType, onSignInClick) {
  const wrap = document.createElement("div");
  wrap.className = "mt-4 rounded-xl border border-sage-100 bg-sage-50/60 p-4";

  if (!isAuthed()) {
    wrap.innerHTML = `
      <div class="flex items-start gap-3">
        <span class="text-lg leading-none">📌</span>
        <div class="min-w-0">
          <p class="text-sm font-medium text-sage-800">Make it yours</p>
          <p class="text-xs text-sage-500 mt-0.5">
            Sign in to pin verbs to your workspace and keep private notes — your reference, your pace.
          </p>
          <button type="button" data-signin
            class="mt-2 text-xs font-medium text-sage-700 underline underline-offset-2 hover:text-sage-900">
            Sign in or create an account
          </button>
        </div>
      </div>`;
    wrap.querySelector("[data-signin]")?.addEventListener("click", () => onSignInClick?.());
    return wrap;
  }

  const pinned = !!item.is_pinned;
  wrap.innerHTML = `
    <div class="flex items-center justify-between gap-3">
      <p class="text-sm font-medium text-sage-800">Your workspace</p>
      <button type="button" data-pin
        class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition
        ${pinned
          ? "bg-sage-500 text-white border-sage-500"
          : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"}">
        <span data-pin-icon>${pinned ? "📌" : "📍"}</span>
        <span data-pin-label>${pinned ? "Pinned" : "Pin to workspace"}</span>
      </button>
    </div>
    <label class="block mt-3">
      <span class="text-xs text-sage-500">Personal notes</span>
      <textarea data-notes rows="3"
        class="mt-1 w-full rounded-lg border border-sage-200 focus:border-sage-500 focus:ring-2 focus:ring-sage-200 outline-none text-sm p-2 resize-y"
        placeholder="Mnemonics, example sentences, anything that helps you remember…">${item.user_notes_text || ""}</textarea>
      <span data-save-status class="text-[11px] text-sage-400"></span>
    </label>`;

  // Pin toggle.
  const pinBtn = wrap.querySelector("[data-pin]");
  let isPinned = pinned;
  pinBtn?.addEventListener("click", async () => {
    pinBtn.disabled = true;
    try {
      await setPinned(collectionType, item.id, !isPinned);
      isPinned = !isPinned;
      item.is_pinned = isPinned;
      wrap.querySelector("[data-pin-icon]").textContent = isPinned ? "📌" : "📍";
      wrap.querySelector("[data-pin-label]").textContent = isPinned ? "Pinned" : "Pin to workspace";
      pinBtn.className = `inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition ${
        isPinned ? "bg-sage-500 text-white border-sage-500" : "bg-white text-sage-600 border-sage-200 hover:border-sage-300"
      }`;
    } catch (err) {
      console.error(err);
    } finally {
      pinBtn.disabled = false;
    }
  });

  // Auto-saving notes (debounced).
  const ta = wrap.querySelector("[data-notes]");
  const status = wrap.querySelector("[data-save-status]");
  const persist = debounce(async (value) => {
    status.textContent = "Saving…";
    try {
      await saveNotes(collectionType, item.id, value);
      item.user_notes_text = value;
      status.textContent = "Saved ✓";
      setTimeout(() => (status.textContent = ""), 1500);
    } catch (err) {
      console.error(err);
      status.textContent = "Couldn't save";
    }
  }, 700);
  ta?.addEventListener("input", (e) => {
    status.textContent = "Editing…";
    persist(e.target.value);
  });

  return wrap;
}
